/**
 * Product Media Gallery Swiper
 * Handles Swiper initialization and interactions for product media galleries
 */

class ProductMediaGallerySwiper {
  constructor(element) {
    this.element = element;
    this.mainSwiper = null;
    this.thumbSwiper = null;
    this.thumbnailPosition = element.dataset.thumbnailPosition || 'bottom';
    this.presentationMode = element.dataset.presentation || 'carousel';

    this.init();
  }

  init() {
    // Always initialize Swiper since mobile always uses carousel
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.initializeThumbnails();
    this.initializeMainSwiper();
    this.setupZoomHandlers();
    this.setupVariantChangeListener();
    this.initializeVariantFilter();
  }

  initializeVariantFilter() {
    // Get the initially selected variant from the variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const variantData = variantPicker.querySelector('script[type="application/json"]');
      if (variantData) {
        try {
          const variant = JSON.parse(variantData.textContent);
          if (variant && variant.id) {
            this.filterSlidesByVariant(variant.id);
          }
        } catch (e) {
          console.error('Error parsing initial variant:', e);
        }
      }
    }
  }

  initializeThumbnails() {
    const thumbElement = this.element.querySelector('[data-swiper-thumbnails]');
    if (!thumbElement) return;

    const isVertical = this.thumbnailPosition === 'left' || this.thumbnailPosition === 'right';

    this.thumbSwiper = new Swiper(thumbElement, {
      spaceBetween: 8,
      slidesPerView: 'auto',
      freeMode: true,
      watchSlidesProgress: true,
      direction: window.innerWidth >= 750 && isVertical ? 'vertical' : 'horizontal',
      breakpoints: {
        0: {
          direction: 'horizontal',
          slidesPerView: 4,
        },
        750: {
          direction: isVertical ? 'vertical' : 'horizontal',
          slidesPerView: isVertical ? 5 : 6,
        }
      }
    });
  }

  forceLoadAllImages() {
    const imgs = this.element.querySelectorAll('[data-swiper-main] .swiper-slide img');
    imgs.forEach(img => {
      if (!img.complete || img.naturalWidth === 0) {
        img.decode().catch(() => {});
      }
    });
  }

  initializeMainSwiper() {
    const mainElement = this.element.querySelector('[data-swiper-main]');
    if (!mainElement) return;

    const config = {
      spaceBetween: 10,
      slidesPerView: 1.2,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      keyboard: {
        enabled: true,
      },
      a11y: {
        enabled: true,
      },
      on: {
        afterInit: () => {
          this.forceLoadAllImages();
        },
        slideChange: (swiper) => {
          this.updateCounter(swiper);
          this.updateVariantFromSlide(swiper);
        },
      },
    };

    // Add pagination if not using thumbnails
    const paginationElement = mainElement.querySelector('.swiper-pagination');
    if (paginationElement) {
      config.pagination = {
        el: '.swiper-pagination',
        clickable: true,
        // dynamicBullets: true,
      };
    }

    // Add thumbnail sync if thumbnails exist
    if (this.thumbSwiper) {
      config.thumbs = {
        swiper: this.thumbSwiper,
      };
    }

    this.mainSwiper = new Swiper(mainElement, config);

    // Initialize counter if it exists
    this.updateCounter(this.mainSwiper);
  }

  updateCounter(swiper) {
    const counterCurrent = this.element.querySelector('.swiper-counter-current');
    const counterTotal = this.element.querySelector('.swiper-counter-total');

    if (counterCurrent && counterTotal) {
      counterCurrent.textContent = swiper.activeIndex + 1;
      counterTotal.textContent = swiper.slides.length;
    }
  }

  setupZoomHandlers() {
    const zoomButtons = this.element.querySelectorAll('[data-zoom-trigger]');

    zoomButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const index = parseInt(button.dataset.zoomTrigger);
        this.openZoomDialog(index, e);
      });
    });
  }

  openZoomDialog(index, event) {
    // Find the zoom dialog within the current gallery or its parent
    const zoomDialog = this.element.querySelector('zoom-dialog') ||
                       this.element.parentElement?.querySelector('zoom-dialog');

    if (zoomDialog && typeof zoomDialog.open === 'function') {
      zoomDialog.open(index, event || new Event('click'));
    }
  }

  setupVariantChangeListener() {
    // Event listener will be set up globally, not per instance
    // See bottom of file for global listener setup
  }

  filterSlidesByVariant(variantId) {
    if (!variantId) return;

    // Re-query the gallery element in case it was replaced
    const currentGallery = document.querySelector('[data-swiper-gallery]');
    if (!currentGallery) return;

    // Filter Swiper slides - use current gallery in DOM, not this.element
    const slides = currentGallery.querySelectorAll('[data-swiper-main] .swiper-slide');

    slides.forEach((slide) => {
      const slideVariantId = slide.dataset.variantId;
      const isCommon = slide.dataset.isCommon === 'true';

      // Show slide if: it's a common image OR it matches the current variant
      const shouldShow = isCommon || slideVariantId === String(variantId);

      if (shouldShow) {
        slide.style.display = '';
        slide.classList.remove('variant-hidden');
      } else {
        slide.style.display = 'none';
        slide.classList.add('variant-hidden');
      }
    });

    // Filter Grid mode slides
    const gridItems = currentGallery.querySelectorAll('.media-gallery__grid li');
    gridItems.forEach((item) => {
      const itemVariantId = item.dataset.variantId;
      const isCommon = item.dataset.isCommon === 'true';

      const shouldShow = isCommon || itemVariantId === String(variantId);

      if (shouldShow) {
        item.style.display = '';
        item.classList.remove('variant-hidden');
      } else {
        item.style.display = 'none';
        item.classList.add('variant-hidden');
      }
    });
  }

  goToMediaById(mediaId) {
    const slides = this.element.querySelectorAll('[data-swiper-main] .swiper-slide');

    slides.forEach((slide, index) => {
      const mediaElement = slide.querySelector('[data-media-id]');
      if (mediaElement && mediaElement.dataset.mediaId === String(mediaId)) {
        this.mainSwiper.slideTo(index);
      }
    });
  }

  updateVariantFromSlide(swiper) {
    // Get the current slide's media ID
    const currentSlide = swiper.slides[swiper.activeIndex];
    const mediaElement = currentSlide?.querySelector('[data-media-id]');

    if (mediaElement) {
      const mediaId = mediaElement.dataset.mediaId;
      // Dispatch event for other components that might need to know about media change
      document.dispatchEvent(new CustomEvent('media:change', {
        detail: { mediaId }
      }));
    }
  }

  destroy() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
    }
    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
    }
  }

  // Static method to filter slides without needing a class instance
  static filterGallerySlides(galleryElement, variantId) {
    if (!variantId || !galleryElement) return;

    // Filter Swiper slides
    const slides = galleryElement.querySelectorAll('[data-swiper-main] .swiper-slide');

    slides.forEach((slide) => {
      const slideVariantId = slide.dataset.variantId;
      const isCommon = slide.dataset.isCommon === 'true';
      const shouldShow = isCommon || slideVariantId === String(variantId);

      if (shouldShow) {
        slide.style.display = '';
        slide.classList.remove('variant-hidden');
      } else {
        slide.style.display = 'none';
        slide.classList.add('variant-hidden');
      }
    });

    // Filter Grid mode slides
    const gridItems = galleryElement.querySelectorAll('.media-gallery__grid li');
    gridItems.forEach((item) => {
      const itemVariantId = item.dataset.variantId;
      const isCommon = item.dataset.isCommon === 'true';
      const shouldShow = isCommon || itemVariantId === String(variantId);

      if (shouldShow) {
        item.style.display = '';
        item.classList.remove('variant-hidden');
      } else {
        item.style.display = 'none';
        item.classList.add('variant-hidden');
      }
    });

    // Re-initialize Swiper to recognize only visible slides
    if (galleryElement.swiperInstance && galleryElement.swiperInstance.mainSwiper) {
      // Destroy old instance
      galleryElement.swiperInstance.mainSwiper.destroy(false, false);
      galleryElement.swiperInstance.destroy();
      delete galleryElement.swiperInstance;

      // Create new instance that will only see visible slides
      const newInstance = new ProductMediaGallerySwiper(galleryElement);
      galleryElement.swiperInstance = newInstance;
    } else {
      // Initialize new instance
      const newInstance = new ProductMediaGallerySwiper(galleryElement);
      galleryElement.swiperInstance = newInstance;
    }
  }
}

// Initialize all galleries when DOM is ready
function initializeGalleries() {
  const galleries = document.querySelectorAll('[data-swiper-gallery]');

  galleries.forEach(gallery => {
    if (!gallery.swiperInstance) {
      gallery.swiperInstance = new ProductMediaGallerySwiper(gallery);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGalleries);
} else {
  initializeGalleries();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const galleries = event.target.querySelectorAll('[data-swiper-gallery]');
  galleries.forEach(gallery => {
    if (gallery.swiperInstance) {
      gallery.swiperInstance.destroy();
    }
    gallery.swiperInstance = new ProductMediaGallerySwiper(gallery);
  });
});

// Cleanup on Shopify section unload
document.addEventListener('shopify:section:unload', (event) => {
  const galleries = event.target.querySelectorAll('[data-swiper-gallery]');
  galleries.forEach(gallery => {
    if (gallery.swiperInstance) {
      gallery.swiperInstance.destroy();
      delete gallery.swiperInstance;
    }
  });
});

// Handle window resize for responsive behavior
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    const galleries = document.querySelectorAll('[data-swiper-gallery]');
    galleries.forEach(gallery => {
      if (gallery.swiperInstance) {
        gallery.swiperInstance.mainSwiper?.update();
        gallery.swiperInstance.thumbSwiper?.update();
      }
    });
  }, 250);
});

// Global variant change listener - filters ALL galleries when variant changes
document.addEventListener('variant:update', (event) => {
  const variant = event.detail?.resource;
  if (variant) {
    // IMPORTANT: The media-gallery.js component replaces the entire gallery HTML
    // after the variant:update event. We need to wait for that to complete.
    setTimeout(() => {
      // Find all gallery instances and filter them
      const galleries = document.querySelectorAll('[data-swiper-gallery]');

      galleries.forEach((gallery) => {
        if (gallery.swiperInstance) {
          gallery.swiperInstance.filterSlidesByVariant(variant.id);
        } else {
          // Manually filter this gallery even without instance
          ProductMediaGallerySwiper.filterGallerySlides(gallery, variant.id);
        }
      });
    }, 150);
  }
});

export default ProductMediaGallerySwiper;
